// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import {
  useFetchTracesQuery,
  useLazyFetchTracesQuery,
} from 'apis/purchase/purchaseApi';
import TableLoading from 'components/atoms/TableLoading';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import PurchaseTemplate from 'components/templates/PurchaseTemplate';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Path } from 'routes';
import { PurchaseTrace } from 'types/purchase';
import { DATE_FORMAT, DEFAULT_PAGE, PAGE_SIZE } from 'util/ConstantValues';
import Csv from 'util/csv';
import styles from './Traceability.module.scss';
import { normalizeNumber, uniqRandomNumber } from 'util/commons';
import dayjs from 'dayjs';
import _snakeCase from 'lodash/snakeCase';
import { PurchaseTraceStatus } from 'util/Enums';
import { Form } from 'react-final-form';
import { InputDataListField } from 'components/molecules/InputDataListField';

const Traceability = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [traceData, setTraceData] = useState<PurchaseTrace[]>([]);
  const [args, setArgs] = useState<{
    page: number;
    take: number;
    search: string;
    trigger?: number;
  }>({
    page: DEFAULT_PAGE,
    take: PAGE_SIZE,
    search: '',
  });
  const { data: traces } = useFetchTracesQuery(args);
  const [trigger] = useLazyFetchTracesQuery();

  useEffect(() => {
    if (args.page === 1) {
      setTraceData(traces?.data || []);
    } else {
      setTraceData([...traceData, ...(traces?.data || [])]);
    }
  }, [traces]);

  const handleDetail = ({
    procureId,
    lastProcessingObjectId,
    materialName,
    id,
  }: PurchaseTrace) => {
    history.push(
      `${Path.purchaseTraces}/${
        procureId || id || lastProcessingObjectId
      }?materialName=${materialName}`
    );
  };

  const headers: (string | [string, string])[] = useMemo(() => {
    return [
      ['status', t('purchase_page.traceability.status')],
      ['materialName', t('purchase_page.traceability.material')],
      ['procureId', t('purchase_page.traceability.procure_id')],
      ['weight', t('purchase_page.traceability.weight') + ' (kg)'],
      ['procurementDate', t('purchase_page.traceability.procurement_date')],
      ['manufacturer', t('purchase_page.traceability.manufacturer')],
    ];
  }, [t]);

  const handleDownLoadCsv = async () => {
    const result = await trigger().unwrap();
    if (result?.data) {
      new Csv({
        headers: headers,
        data:
          result.data.map((trace) => ({
            status: t(
              `purchase_page.traceability.status_label.${_snakeCase(
                trace.status
              )}`
            ),
            materialName: trace.materialName,
            procureId: trace.procureId,
            weight:
              trace.weight != null
                ? normalizeNumber({ value: trace.weight })
                : '',
            procurementDate: trace.procurementDate
              ? dayjs(trace.procurementDate).format(DATE_FORMAT.slaYMDHm)
              : '',
            manufacturer: trace.manufacturer,
          })) || [],
      }).downLoadCsv({
        filename: t('purchase_page.traceability.fileName'),
      });
    }
  };

  const hasTraceData = traceData.length > 0;

  const tableHeader = (
    <thead>
      <tr>
        <th className={styles.td110}>
          {t('purchase_page.traceability.status')}
        </th>
        <th className={styles.td200}>
          {t('purchase_page.traceability.material')}
        </th>
        <th className={styles.td200}>
          {t('purchase_page.traceability.procure_id')}
        </th>
        <th className={styles.td110}>
          {t('purchase_page.traceability.weight')}
        </th>
        <th className={styles.td110}>
          {t('purchase_page.traceability.procurement_date')}
        </th>
        <th className={styles.td200}>
          {t('purchase_page.traceability.manufacturer')}
        </th>
      </tr>
    </thead>
  );

  return (
    <PurchaseTemplate title={t('purchase_nav.traceability')}>
      <div className={styles.traceability}>
        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.title}>{t('purchase_nav.traceability')}</div>
            <div className={styles.actions}>
              <button
                className={styles.downloadCsv}
                onClick={() => handleDownLoadCsv()}
              >
                <IconDownload />
                {t('purchase_page.traceability.download_csv')}
              </button>
            </div>
          </div>

          <div className={styles.searchContainer}>
            <Form<{ search: string }>
              onSubmit={({ search }) =>
                setArgs((prev) => ({
                  ...prev,
                  search,
                  page: 1,
                  trigger: uniqRandomNumber(),
                }))
              }
            >
              {({ handleSubmit }) => (
                <form onSubmit={handleSubmit} className={styles.searchForm}>
                  <InputDataListField
                    name="search"
                    isDarkMode
                    className={styles.inputDataListField}
                    textWrapperStyle={{
                      marginBottom: 0,
                    }}
                    placeholder={t('purchase_page.traceability.search_id')}
                  />

                  <button type="submit" className={styles.searchButton}>
                    {t('common.button.search')}
                  </button>
                </form>
              )}
            </Form>
          </div>

          <div className={styles.emptyWrapper}>
            <table className={`primary-table ${styles.primaryTable}`}>
              {tableHeader}

              {hasTraceData && (
                <tbody>
                  {traceData.map((trace, i) => {
                    const isProcured =
                      trace.status === PurchaseTraceStatus.Procured;

                    return (
                      <tr key={i} onClick={() => handleDetail(trace)}>
                        <td className={styles.td110}>
                          <div
                            style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: 4,
                              background: isProcured ? '#D2FDC6' : '#FFFFFF',
                              color: '#000000',
                            }}
                          >
                            {t(
                              `purchase_page.traceability.status_label.${_snakeCase(
                                trace.status
                              )}`
                            )}
                          </div>
                        </td>
                        <td className={styles.td200}>{trace.materialName}</td>
                        <td className={styles.td200}>
                          {trace.procureId || '-'}
                        </td>
                        <td className={styles.td110}>
                          {trace.weight != null && isProcured ? (
                            <strong>
                              {normalizeNumber({ value: trace.weight })}kg
                            </strong>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className={styles.td110}>
                          {trace.procurementDate && isProcured
                            ? dayjs(trace.procurementDate).format(
                                DATE_FORMAT.slaYMDHm
                              )
                            : '-'}
                        </td>
                        <td className={styles.td200}>
                          {trace.manufacturer && isProcured
                            ? trace.manufacturer
                            : '-'}
                        </td>
                      </tr>
                    );
                  })}

                  <TableLoading
                    onNext={() => {
                      if (
                        args.page * PAGE_SIZE <
                        (traces?.total || PAGE_SIZE)
                      ) {
                        setArgs({
                          ...args,
                          page: args.page + 1,
                        });
                      }
                    }}
                  />
                </tbody>
              )}
            </table>

            {!hasTraceData && (
              <div className={styles.nodata}>{t('messages.M_008')}</div>
            )}
          </div>
        </div>
      </div>
    </PurchaseTemplate>
  );
};

export default Traceability;
